/* eslint-disable no-restricted-imports */
import { Button, Flex } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import React from "react";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import {
  NavigationBlocker,
  useNavigationBlockerContext,
} from "src/components/NavigationBlocker";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import * as AF from "src/types/formTemplate";
import * as RD from "src/types/remoteData";
import { getNewVerificationId, isNewVerification } from "../../../contents";
import { useEditFormTemplateContext } from "../../../context";
import { ImportError } from "../../ImportError";
import { LiveEditConfirmationProvider } from "../../LiveEditConfirmation";
import { ValidationAlert } from "../../ValidationAlert";
import { DirtyFormTracker } from "../DirtyFormTracker";
import {
  FormikType,
  toDeletedVerificationIds,
  toFormikValues,
  toQuestion,
  toVerification,
  validateForm,
} from "../formik";
import { ConfigureQuestion } from "../QuestionConfiguration/ConfigureQuestion";
import { SchoolItem } from "../types";
import { AnswersOptions } from "./AnswersOptions";
import { QuestionFormat } from "./QuestionContraints";
import { QuestionKey } from "./QuestionKey";
import { QuestionTitle } from "./QuestionTitle";
import { QuestionType } from "./QuestionType";

export type QuestionFormEditProps = {
  schools: RD.RemoteData<unknown, SchoolItem[]>;
  onClose: () => void;

  onUpdate: (
    question: AF.V3.Question,
    verification: AF.V3.FormVerification | undefined,
    deletedVerificationIds: string[] | undefined
  ) => Promise<void>;
  question: AF.V3.Question;
  sectionType: AF.V3.SectionType;
  otherQuestions: readonly AF.V3.Question[];
  isOpen: boolean;
};

export function QuestionFormEditDrawer({
  onClose,
  onUpdate,
  schools,
  question,
  sectionType,
  otherQuestions,
  isOpen,
}: QuestionFormEditProps) {
  const { state } = useEditFormTemplateContext();
  const verifications: AF.V3.FormVerification[] = React.useMemo(
    () => state.get("draft").verifications ?? [],
    [state]
  );
  const initialValues: FormikType = React.useMemo(
    () => toFormikValues(sectionType, question, verifications),
    [question, verifications, sectionType]
  );

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const toast = useAvelaToast();
  const { grades } = useEditFormTemplateContext();

  const { confirm, hasChanges } = useNavigationBlockerContext();
  const onCancelHandler = React.useCallback(async () => {
    if (!hasChanges || (await confirm())) {
      onClose();
    }
  }, [confirm, hasChanges, onClose]);

  const onSubmit = React.useCallback(
    async (values: FormikType) => {
      setIsSubmitting(true);

      const updatedQuestion = toQuestion(question, values, grades);
      const updatedVerification = toVerification(values);
      const deletedVerificationIds = toDeletedVerificationIds(values);

      // if the verification is new, we need to generate a new id
      // and update the question with the new verification id
      if (updatedVerification && isNewVerification(updatedVerification)) {
        updatedVerification.id = getNewVerificationId();
        updatedQuestion.formVerificationId = updatedVerification.id;
      }

      try {
        await onUpdate(
          updatedQuestion,
          updatedVerification,
          deletedVerificationIds
        );
      } catch (err: unknown) {
        console.error(err);
        toast.genericError();
      } finally {
        setIsSubmitting(false);
        document.getElementById(question.id)?.scrollIntoView();
      }
    },
    [grades, onUpdate, question, toast]
  );

  return (
    <ResizableDrawer
      isOpen={isOpen}
      onClose={onCancelHandler}
      contentProps={{
        initialWidthPercent: 50,
      }}
    >
      <ValidationAlert id={question.id} />
      <ImportError id={question.id} />
      <Formik<FormikType>
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validateForm(sectionType, state.get("draft"))}
      >
        {(formik) => {
          return (
            <LiveEditConfirmationProvider questionId={question.id}>
              <ResizableDrawer.Content as={Form} noValidate>
                <NavigationBlocker hasChanges={formik.dirty} />
                <ResizableDrawer.Header>Edit question</ResizableDrawer.Header>
                <ResizableDrawer.Body>
                  <Flex direction="column" gap="6">
                    <DirtyFormTracker />
                    <QuestionTitle questionId={question.id} />
                    <QuestionKey
                      questionId={question.id}
                      otherQuestions={otherQuestions}
                    />
                    <QuestionType
                      questionId={question.id}
                      sectionType={sectionType}
                    />
                    <QuestionFormat questionId={question.id} />
                    <AnswersOptions
                      question={question}
                      otherQuestions={otherQuestions}
                    />
                    <ConfigureQuestion
                      questionId={question.id}
                      sectionType={sectionType}
                      otherQuestions={otherQuestions}
                      schools={schools}
                    />
                  </Flex>
                </ResizableDrawer.Body>
                <ResizableDrawer.Footer>
                  <Button
                    variant="outline"
                    colorScheme="gray"
                    onClick={onCancelHandler}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" isLoading={isSubmitting}>
                    Update question
                  </Button>
                </ResizableDrawer.Footer>
              </ResizableDrawer.Content>
            </LiveEditConfirmationProvider>
          );
        }}
      </Formik>
    </ResizableDrawer>
  );
}
